import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Bell, Zap, Package, Share, MoreVertical, PlusSquare, Check, Smartphone, Monitor, ChevronDown, ChevronUp, Download, X, ArrowLeft } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';

const InstallAppPage = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [expandedSection, setExpandedSection] = useState(null);

  useEffect(() => {
    // Detect device type
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    setIsIOS(/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream);
    setIsAndroid(/android/i.test(userAgent));
    
    // Check if already installed as PWA
    const isInStandaloneMode = window.matchMedia('(display-mode: standalone)').matches 
      || window.navigator.standalone 
      || document.referrer.includes('android-app://');
    setIsStandalone(isInStandaloneMode);
  }, []);

  const translations = {
    en: {
      title: "Install the App",
      subtitle: "Get the best experience, receive instant notifications, and access the marketplace faster than ever.",
      already_installed: "You're already using the app!",
      already_installed_desc: "Great! You've already installed the marketplace on your device. Enjoy the full experience!",
      back_to_home: "Back to Home",
      maybe_later: "Maybe Later",
      benefits_title: "Why Install?",
      benefit_notifications: "Notifications",
      benefit_notifications_desc: "Never miss a bid or message again.",
      benefit_speed: "Lightning Fast",
      benefit_speed_desc: "No browser bars, right on your home screen.",
      benefit_lightweight: "Lightweight",
      benefit_lightweight_desc: "No heavy downloads from the App Store needed.",
      ios_title: "iPhone & iPad (Safari)",
      android_title: "Android (Chrome)",
      desktop_title: "Desktop (Chrome/Edge)",
      ios_step1: "Tap the Share button",
      ios_step1_detail: "at the bottom of your screen",
      ios_step2: "Scroll down and tap",
      ios_step2_action: "'Add to Home Screen'",
      ios_step3: "Tap 'Add' in the top right",
      ios_step3_detail: "to confirm",
      android_step1: "Tap the download icon",
      android_step1_detail: "in the address bar (or menu ⋮ at the bottom)",
      android_step2: "Tap",
      android_step2_action: "'Install app'",
      android_step3: "Tap 'Install'",
      android_step3_detail: "to confirm",
      desktop_step1: "Click the install icon",
      desktop_step1_detail: "in the address bar (or menu)",
      desktop_step2: "Click",
      desktop_step2_action: "'Install'",
      desktop_step3: "The app will open",
      desktop_step3_detail: "in its own window",
      tip_title: "Pro Tip",
      tip_ios: "Make sure you're using Safari - other browsers on iOS don't support this feature.",
      tip_android: "Look for the download arrow ↓ in the address bar. If you don't see it, tap the menu (⋮) at the bottom of the screen.",
      your_device: "Your Device",
      other_devices: "Other Devices",
      show_instructions: "Show instructions",
      hide_instructions: "Hide instructions",
    },
    nl: {
      title: "Installeer de App",
      subtitle: "Krijg de beste ervaring, ontvang direct notificaties en open de marktplaats sneller dan ooit.",
      already_installed: "Je gebruikt de app al!",
      already_installed_desc: "Top! Je hebt de marktplaats al op je apparaat geïnstalleerd. Geniet van de volledige ervaring!",
      back_to_home: "Terug naar Home",
      maybe_later: "Misschien Later",
      benefits_title: "Waarom Installeren?",
      benefit_notifications: "Notificaties",
      benefit_notifications_desc: "Mis nooit meer een bod of bericht.",
      benefit_speed: "Supersnel",
      benefit_speed_desc: "Geen browserbalken, direct op je beginscherm.",
      benefit_lightweight: "Lichtgewicht",
      benefit_lightweight_desc: "Geen zware downloads uit de App Store nodig.",
      ios_title: "iPhone & iPad (Safari)",
      android_title: "Android (Chrome)",
      desktop_title: "Desktop (Chrome/Edge)",
      ios_step1: "Tik op de Deel-knop",
      ios_step1_detail: "onderaan je scherm",
      ios_step2: "Scroll naar beneden en tik op",
      ios_step2_action: "'Zet op beginscherm'",
      ios_step3: "Tik op 'Voeg toe'",
      ios_step3_detail: "rechtsboven om te bevestigen",
      android_step1: "Tik op het download-icoon",
      android_step1_detail: "in de adresbalk (of menu ⋮ onderaan)",
      android_step2: "Tik op",
      android_step2_action: "'App installeren'",
      android_step3: "Tik op 'Installeren'",
      android_step3_detail: "om te bevestigen",
      desktop_step1: "Klik op het installeer-icoon",
      desktop_step1_detail: "in de adresbalk (of menu)",
      desktop_step2: "Klik op",
      desktop_step2_action: "'Installeren'",
      desktop_step3: "De app opent",
      desktop_step3_detail: "in een eigen venster",
      tip_title: "Tip",
      tip_ios: "Zorg dat je Safari gebruikt - andere browsers op iOS ondersteunen dit niet.",
      tip_android: "Zoek naar de download-pijl ↓ in de adresbalk. Zie je die niet? Tik op het menu (⋮) onderaan het scherm.",
      your_device: "Jouw Apparaat",
      other_devices: "Andere Apparaten",
      show_instructions: "Toon instructies",
      hide_instructions: "Verberg instructies",
    },
    es: {
      title: "Instalar la App",
      subtitle: "Obtén la mejor experiencia, recibe notificaciones instantáneas y accede al mercado más rápido que nunca.",
      already_installed: "¡Ya estás usando la app!",
      already_installed_desc: "¡Genial! Ya has instalado el mercado en tu dispositivo. ¡Disfruta de la experiencia completa!",
      back_to_home: "Volver al Inicio",
      maybe_later: "Quizás Después",
      benefits_title: "¿Por qué Instalar?",
      benefit_notifications: "Notificaciones",
      benefit_notifications_desc: "No te pierdas nunca una oferta o mensaje.",
      benefit_speed: "Súper Rápido",
      benefit_speed_desc: "Sin barras del navegador, directo en tu pantalla.",
      benefit_lightweight: "Ligero",
      benefit_lightweight_desc: "Sin descargas pesadas de la App Store.",
      ios_title: "iPhone & iPad (Safari)",
      android_title: "Android (Chrome)",
      desktop_title: "Escritorio (Chrome/Edge)",
      ios_step1: "Toca el botón Compartir",
      ios_step1_detail: "en la parte inferior de la pantalla",
      ios_step2: "Desplázate y toca",
      ios_step2_action: "'Añadir a pantalla de inicio'",
      ios_step3: "Toca 'Añadir'",
      ios_step3_detail: "arriba a la derecha para confirmar",
      android_step1: "Toca el icono de descarga",
      android_step1_detail: "en la barra de direcciones (o menú ⋮ abajo)",
      android_step2: "Toca",
      android_step2_action: "'Instalar aplicación'",
      android_step3: "Toca 'Instalar'",
      android_step3_detail: "para confirmar",
      desktop_step1: "Haz clic en el icono de instalar",
      desktop_step1_detail: "en la barra de direcciones (o menú)",
      desktop_step2: "Haz clic en",
      desktop_step2_action: "'Instalar'",
      desktop_step3: "La app se abrirá",
      desktop_step3_detail: "en su propia ventana",
      tip_title: "Consejo",
      tip_ios: "Asegúrate de usar Safari - otros navegadores en iOS no soportan esta función.",
      tip_android: "Busca la flecha de descarga ↓ en la barra de direcciones. Si no la ves, toca el menú (⋮) en la parte inferior.",
      your_device: "Tu Dispositivo",
      other_devices: "Otros Dispositivos",
      show_instructions: "Mostrar instrucciones",
      hide_instructions: "Ocultar instrucciones",
    }
  };

  const txt = translations[language] || translations.en;

  // If already installed, show success message
  if (isStandalone) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="w-10 h-10 text-green-500" />
          </div>
          <h1 className="text-2xl font-bold text-foreground mb-3">{txt.already_installed}</h1>
          <p className="text-muted-foreground mb-8">{txt.already_installed_desc}</p>
          <Link 
            to="/"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-xl font-medium hover:bg-primary/90 transition-colors"
          >
            {txt.back_to_home}
          </Link>
        </div>
      </div>
    );
  }

  const InstructionStep = ({ number, icon: Icon, title, detail, action }) => (
    <div className="flex items-start gap-4">
      <div className="flex-shrink-0 w-8 h-8 bg-primary/20 rounded-full flex items-center justify-center text-primary font-bold text-sm">
        {number}
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-2 text-foreground font-medium">
          {Icon && <Icon className="w-4 h-4 text-primary" />}
          <span>{title}</span>
          {action && <span className="text-primary font-semibold">{action}</span>}
        </div>
        {detail && <p className="text-sm text-muted-foreground mt-0.5">{detail}</p>}
      </div>
    </div>
  );

  const DeviceInstructions = ({ title, steps, tip, isCurrentDevice, icon: DeviceIcon }) => {
    const isExpanded = expandedSection === title || isCurrentDevice;
    
    return (
      <div className={`bg-card border rounded-2xl overflow-hidden transition-all ${
        isCurrentDevice ? 'border-primary/50 ring-2 ring-primary/20' : 'border-border'
      }`}>
        <button
          onClick={() => setExpandedSection(isExpanded && !isCurrentDevice ? null : title)}
          className="w-full p-4 sm:p-6 flex items-center justify-between text-left"
        >
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
              isCurrentDevice ? 'bg-primary/20 text-primary' : 'bg-secondary text-muted-foreground'
            }`}>
              <DeviceIcon className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-semibold text-foreground">{title}</h3>
              {isCurrentDevice && (
                <span className="text-xs text-primary font-medium">{txt.your_device}</span>
              )}
            </div>
          </div>
          {!isCurrentDevice && (
            isExpanded ? <ChevronUp className="w-5 h-5 text-muted-foreground" /> : <ChevronDown className="w-5 h-5 text-muted-foreground" />
          )}
        </button>
        
        {isExpanded && (
          <div className="px-4 sm:px-6 pb-6 space-y-4">
            <div className="h-px bg-border -mx-4 sm:-mx-6 mb-4" />
            <div className="space-y-4">
              {steps.map((step, index) => (
                <InstructionStep key={index} number={index + 1} {...step} />
              ))}
            </div>
            {tip && (
              <div className="mt-4 p-3 bg-amber-500/10 border border-amber-500/20 rounded-xl">
                <p className="text-sm text-amber-600 dark:text-amber-400">
                  <strong>{txt.tip_title}:</strong> {tip}
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const iosSteps = [
    { icon: Share, title: txt.ios_step1, detail: txt.ios_step1_detail },
    { title: txt.ios_step2, action: txt.ios_step2_action },
    { icon: PlusSquare, title: txt.ios_step3, detail: txt.ios_step3_detail },
  ];

  const androidSteps = [
    { icon: Download, title: txt.android_step1, detail: txt.android_step1_detail },
    { title: txt.android_step2, action: txt.android_step2_action },
    { icon: Check, title: txt.android_step3, detail: txt.android_step3_detail },
  ];

  const desktopSteps = [
    { icon: Download, title: txt.desktop_step1, detail: txt.desktop_step1_detail },
    { title: txt.desktop_step2, action: txt.desktop_step2_action },
    { title: txt.desktop_step3, detail: txt.desktop_step3_detail },
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="install-app-page">
      {/* Navbar */}
      <Navbar />
      
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-purple-500/10" />
        <div className="relative max-w-4xl mx-auto px-4 py-12 sm:py-16 text-center">
          {/* Maybe Later button */}
          <button
            onClick={() => navigate('/marketplace')}
            className="absolute top-4 right-4 px-4 py-2 text-sm text-muted-foreground hover:text-foreground bg-secondary/50 hover:bg-secondary rounded-full transition-colors flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            {txt.maybe_later}
          </button>
          
          <div className="w-16 h-16 bg-primary/20 rounded-2xl flex items-center justify-center mx-auto mb-6 rotate-3">
            <Smartphone className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-4">
            {txt.title}
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {txt.subtitle}
          </p>
        </div>
      </div>

      {/* Benefits */}
      <div className="max-w-4xl mx-auto px-4 pb-8">
        <h2 className="text-xl font-semibold text-foreground mb-4 text-center">{txt.benefits_title}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-card border border-border rounded-2xl p-5 text-center hover:border-primary/30 transition-colors">
            <div className="w-12 h-12 bg-blue-500/10 rounded-xl flex items-center justify-center mx-auto mb-3">
              <Bell className="w-6 h-6 text-blue-500" />
            </div>
            <h3 className="font-semibold text-foreground mb-1">{txt.benefit_notifications}</h3>
            <p className="text-sm text-muted-foreground">{txt.benefit_notifications_desc}</p>
          </div>
          
          <div className="bg-card border border-border rounded-2xl p-5 text-center hover:border-primary/30 transition-colors">
            <div className="w-12 h-12 bg-amber-500/10 rounded-xl flex items-center justify-center mx-auto mb-3">
              <Zap className="w-6 h-6 text-amber-500" />
            </div>
            <h3 className="font-semibold text-foreground mb-1">{txt.benefit_speed}</h3>
            <p className="text-sm text-muted-foreground">{txt.benefit_speed_desc}</p>
          </div>
          
          <div className="bg-card border border-border rounded-2xl p-5 text-center hover:border-primary/30 transition-colors">
            <div className="w-12 h-12 bg-green-500/10 rounded-xl flex items-center justify-center mx-auto mb-3">
              <Package className="w-6 h-6 text-green-500" />
            </div>
            <h3 className="font-semibold text-foreground mb-1">{txt.benefit_lightweight}</h3>
            <p className="text-sm text-muted-foreground">{txt.benefit_lightweight_desc}</p>
          </div>
        </div>
      </div>

      {/* Instructions */}
      <div className="max-w-4xl mx-auto px-4 pb-16">
        <div className="space-y-4">
          {/* Show current device first */}
          {isIOS && (
            <DeviceInstructions
              title={txt.ios_title}
              steps={iosSteps}
              tip={txt.tip_ios}
              isCurrentDevice={true}
              icon={Smartphone}
            />
          )}
          {isAndroid && (
            <DeviceInstructions
              title={txt.android_title}
              steps={androidSteps}
              tip={txt.tip_android}
              isCurrentDevice={true}
              icon={Smartphone}
            />
          )}
          {!isIOS && !isAndroid && (
            <DeviceInstructions
              title={txt.desktop_title}
              steps={desktopSteps}
              isCurrentDevice={true}
              icon={Monitor}
            />
          )}

          {/* Other devices section */}
          <h3 className="text-sm font-medium text-muted-foreground pt-4">{txt.other_devices}</h3>
          
          {!isIOS && (
            <DeviceInstructions
              title={txt.ios_title}
              steps={iosSteps}
              tip={txt.tip_ios}
              isCurrentDevice={false}
              icon={Smartphone}
            />
          )}
          {!isAndroid && (
            <DeviceInstructions
              title={txt.android_title}
              steps={androidSteps}
              tip={txt.tip_android}
              isCurrentDevice={false}
              icon={Smartphone}
            />
          )}
          {(isIOS || isAndroid) && (
            <DeviceInstructions
              title={txt.desktop_title}
              steps={desktopSteps}
              isCurrentDevice={false}
              icon={Monitor}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default InstallAppPage;
